import { ControllerInterface } from "csi/action/Controller";
import JsonResponse, { JsonResponseData } from "csi/comm/JsonResponse";
import { ControllerSaveFunction } from "csi/middleware/common";
import { GoToDialogHelper } from "csi/middleware/goToDialog/GoToDialogHelper";
import reflection from "csi/reflection";
import { submitWriter } from "csi/service/writerService";
import { ATTR_SUBTYPE, C8Node, DUMMY, ICache, cnl } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { Button } from "designSystem/index";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { uniqueId, isEqual } from "lodash";
import { ObservableMap, action, computed, makeObservable, observable, runInAction } from "mobx";
import React, { SyntheticEvent, createContext, useContext } from "react";
import { REQUEST_MODULE, REQUEST_OPERATION, createFormData, postFormData } from "src/c8-platform/core/request";
import { BaseCompositionMaterial } from "./BaseCompositionMaterial";
import { CompositionPlacement } from "./CompositionPlacement";
import { MaterialOnBOM } from "./MaterialOnBOM";
import { SalesRegionException } from "./SalesRegionException";
import { numberRound } from "csi/utility/numberUtility";

export const TYPE_SIZE_CHART_ADMIN = "SizeChartAdmin";
export const BASE_COMPOSITION = "Base Composition";
export const TYPE_COMPOSITION = "Composition";


/** create composition Exception key */
export function createSalesRegionExceptionKey(): string {
	return `sales_region_exception_${uniqueId()}`;
}

/** create composition material key */
export function createCompositionPlacementKey(): string {
	return `composition_placement_${uniqueId()}`;
}

/** create composition type key */
export function createBaseCompositionMaterialKey(): string {
	return `base_composition_material_${uniqueId()}`;
}

/** create composition type key */
export function createBOMMaterialKey(): string {
	return `BOM_material_${uniqueId()}`;
}

export function roundToNearestEighth(number: number): number {
	return numberRound(number * .01, 8);
}

/**  Generic function to load different types of nodes */
export const loadNodes = async (operation: string, additionalParams: Record<string, string> = {}): Promise<JsonResponse> => {
	const formData = createFormData({
		[REQUEST_MODULE]: "Specification",
		[REQUEST_OPERATION]: operation,
		...additionalParams,  // Spread the additional parameters into the formData
	});

	const response = await postFormData<JsonResponseData>(formData);
	const jsonResponse = new JsonResponse(response);
	return jsonResponse;
};

class BannerCondition {
	/** Name of Sales Region Exception associated with the banner */
	sreName: string;

	/** Title of the banner */
	bannerTitle: string;

	constructor(name: string, bannerTitle: string) {
		this.sreName = name;
		this.bannerTitle = bannerTitle;
	}
}

export class BaseCompositionStore extends ReactionStore {
	/** action controller to access to be able to change writer */
	readonly actionController: ControllerInterface;

	/** existing nodes to be set on an edit action */
	@observable.ref existingSRENodes: C8Node[] = [];
	@observable.ref existingSRNodes: C8Node[] = [];
	@observable.ref existingSREPlacementNodes: C8Node[] = [];
	@observable.ref existingSREMaterialNodes: C8Node[] = [];
	@observable.ref existingSRETechnicalCompositionNodes: C8Node[] = [];
	@observable.ref existingBaseMaterialNodes: C8Node[] = [];
	@observable.ref existingBasePlacementNodes: C8Node[] = [];
	@observable.ref existingBaseTechnicalCompositionNodes: C8Node[] = [];
	@observable existingRevision: C8Node = null;

	bannerConditions: ObservableMap<string, BannerCondition> = observable.map<string, BannerCondition>(null, { deep: false });

	/** sustainability flag */
	@observable hasSustainability: boolean;

	/** determines if the sales region dialog window should be up */
	@observable showSalesRegionConfirmCloseWindow: boolean = false;

	/** determines if the Save dialog window should be up */
	@observable saveFlag: boolean = false;

	/** determines if dropdown should be auto focused or not */
	@observable focusDropDownBoolean: boolean = true;

	/** stores the delete key when a delete dialog pops up */
	@observable currentSalesRegionDeleteKey: string = "";

	/** cache for use inside the store */
	readonly cache: ICache;

	/** map of all materials so that a query is only run if a new BOM is selected, otherwise it will be in the map */
	materialNodeMap: ObservableMap<cnl, C8Node[]> = observable.map<cnl, C8Node[]>(null, { deep: false });

	/** anchor element for the sales region exception menu */
	@observable.ref menuAnchorEl = null;

	/** anchor element for the sales region exception menu in the Apply To */
	@observable.ref applyToAnchorEl = null;

	/** Set of strings that hold the names that should be checked inside the apply to */
	@observable applyToSalesRegionChecks: Set<string> = new Set();

	/** Locale from useLocale on parent */
	readonly loc: ILocaleProvider;

	/** action for save */
	private readonly _onSave: ControllerSaveFunction; // unused for now

	/** action for save */
	private readonly _onClose: VoidFunction;

	/** all node sets being used */
	@observable.ref apparelBOMNodes = [];
	@observable.ref compositionNodes = [];
	@observable.ref placementNodes = [];
	@observable.ref sustainabilityCompositionNodes = [];
	@observable.ref salesRegions = [];

	/** reference to the current BOM */
	@observable.ref currentSelectedBOM: C8Node = null;

	/** determines if using new from style BOM */
	@observable fromStyleBom = false;

	/** Sales Region Exception map */
	private salesRegionExceptionMap: ObservableMap<string, SalesRegionException> = observable.map<string, SalesRegionException>(null, { deep: false });

	/** fetching AllowZeroPercent SubtypeSetting boolean value*/
	allowZeroPercent: boolean = false;

	/** fetching AllowCompositionEdits SubtypeSetting boolean value*/
	allowCompositionEdit: boolean = false;

	/** name of the current sales region exception */
	readonly currentSalesRegionExceptionName: string = "";

	/** fetching EnforceHundredPercent SubtypeSetting boolean value*/
	@observable enforceHundredPercent: boolean = false;
	/** fetching Language Syntax SubtypeSetting boolean value*/
	languageSyntax: string = "";

	bomGoToDialogHelper: GoToDialogHelper;

	readonly careCompRevNode: C8Node;

	/** index of which tab we are currently on */
	@observable tabIndex: number = 0;

	/** disable Composition Material boxes and related percentages based on allowCompositionEdit subtype attribute value */
	get disableCompositionMaterialEdit(): boolean {
		return this.fromStyleBom && !this.allowCompositionEdit || this.disableBOMMaterialEdit;
	}

	/** enable BOM Material boxes and related percentages based on allowCompositionEdit subtype attribute value */
	get disableBOMMaterialEdit(): boolean {
		return this.fromStyleBom && this.currentSelectedBOM === null;
	}

	/** returns true if input should auto focus */
	get shouldInputAutoFocus(): boolean {
		return this.focusDropDownBoolean;
	}

	/** Confirmation window open when it become true */
	get openOnConfirmDailog(): boolean {
		return this.saveFlag && this.hasZeroPercentNotNestUnder;
	}

	/** Determines if the apply to menu is open */
	get applyToMenuOpen(): boolean {
		return this.applyToAnchorEl != null;
	}

	/** Retrieves an array containing a single ComponentItem representing the DUMMY item. */
	get dummyItemList(): ComponentItem[] {
		return [
			{ id: DUMMY, name: "" },
		];
	}

	/** load the database with the selections and inputs from the view */
	onSave = async (): Promise<boolean> => {
		const { _onSave } = this;
		this.removeDuplicateSalesRegionExceptions();
		const w = new Writer();

		const compositionUrlsAfterAdditions = [];
		const compositionTypes = [];
		const percentages = [];
		const materialCNLs = [];
		const materialPercentages = [];
		const compositionPlacementCNLs = [];
		const compositionPlacementIDs = [];
		const compositionPlacementPercentages = [];
		const exemptFromPercentages: boolean[] = [];
		const parentsalesRegions = [];

		const salesRegions = [];
		const includeCompositionPlacementNames: boolean[] = [];
		const combineSameCompositionMaterials: boolean[] = [];
		const includeCompositionTypes: boolean[] = [];


		// Iterate through each sales Region
		this.salesRegionExceptions.forEach((salesRegionException) => {
			const { compositionPlacements } = salesRegionException;

			// Base and Sales Region specific settings
			salesRegions.push(salesRegionException.currentUrl);
			combineSameCompositionMaterials.push(salesRegionException.combineSameCompositionMaterials);
			includeCompositionTypes.push(salesRegionException.includeCompositionTypes);
			includeCompositionPlacementNames.push(salesRegionException.includeCompositionPlacementNames);
			
			// the first salesRegionException is always the Base and if SalesRegion.currentURL is dummy its a salesregion		
			let i=0;
			compositionPlacements.forEach((placement) => {	
				if (this.fromStyleBom) {
					placement.materialBOMs.forEach((material) => {
						material.baseCompositionMaterials.forEach((composition: BaseCompositionMaterial) => {
							compositionUrlsAfterAdditions.push(composition.currentUrl);
							percentages.push(roundToNearestEighth(composition.percentage));
							if (xml.isValidURL(composition.currentType)) {
								compositionTypes.push(composition.currentType);
							} else {
								compositionTypes.push(DUMMY);
							}
							materialCNLs.push(material.currentUrl);
							materialPercentages.push(roundToNearestEighth(material.percentage));
							compositionPlacementCNLs.push(placement.currentUrl);
							compositionPlacementIDs.push(i);
							compositionPlacementPercentages.push(roundToNearestEighth(placement.percentage));
							exemptFromPercentages.push(composition.nestUnderContainsPlacement);
							parentsalesRegions.push(salesRegionException.currentUrl);
						});
					});
				} else {
					placement.baseCompositionMaterials.forEach((composition: BaseCompositionMaterial) => {
						compositionUrlsAfterAdditions.push(composition.currentUrl);
						percentages.push(roundToNearestEighth(composition.percentage));
						if (xml.isValidURL(composition.currentType)) {
							compositionTypes.push(composition.currentType);
						} else {
							compositionTypes.push(DUMMY);
						}
						materialCNLs.push(DUMMY);
						materialPercentages.push(0);
						compositionPlacementIDs.push(i);
						compositionPlacementCNLs.push(placement.currentUrl);
						compositionPlacementPercentages.push(roundToNearestEighth(placement.percentage));
						exemptFromPercentages.push(composition.nestUnderContainsPlacement);
						parentsalesRegions.push(salesRegionException.currentUrl);
					});
				}
				i++;
			});
		});

		// //ToDo Figure out how to get the CnC node into store
		w.callMethod("Specification", "CreateCareAndCompositionNodes")
			.addParameter("URL", xml.ATTR_CNL, this.careCompRevNode.$URL)
			.addParameter("Compositions", xml.ATTR_REFVECTOR, compositionUrlsAfterAdditions)
			.addParameter("CompositionTypes", xml.ATTR_REFVECTOR, compositionTypes)
			.addParameter("Percentages", xml.ATTR_DOUBLEVECTOR, percentages)
			.addParameter("CompositionPlacements", xml.ATTR_REFVECTOR, compositionPlacementCNLs)
			.addParameter("CompositionPlacementIDs", xml.ATTR_INTEGERVECTOR, compositionPlacementIDs)
			.addParameter("CompositionPlacementPercentages", xml.ATTR_DOUBLEVECTOR, compositionPlacementPercentages)
			.addParameter("Materials", xml.ATTR_REFVECTOR, materialCNLs)
			.addParameter("MaterialPercentages", xml.ATTR_DOUBLEVECTOR, materialPercentages)
			.addParameter("SelectedBOM", xml.ATTR_CNL, this.currentSelectedBOM?.$URL)
			.addParameter("ExemptFromPercentages", xml.ATTR_BOOLEANVECTOR, exemptFromPercentages)
			.addParameter("ParentSalesRegions", xml.ATTR_REFVECTOR, parentsalesRegions)
			.addParameter("SalesRegions", xml.ATTR_REFVECTOR, salesRegions)
			.addParameter("IncludeCompositionTypes", xml.ATTR_BOOLEANVECTOR, includeCompositionTypes)
			.addParameter("IncludeCompositionPlacementNames", xml.ATTR_BOOLEANVECTOR, includeCompositionPlacementNames)
			.addParameter("CombineSameCompositionMaterials", xml.ATTR_BOOLEANVECTOR, combineSameCompositionMaterials);

		try {
			await submitWriter(w);
		} catch (error) {
			return false;
		}
		this._onSave();
		return true;
	};

	/** all the compositions */
	@computed get salesRegionExceptions(): SalesRegionException[] {
		const salesRegionExceptions: SalesRegionException[] = Array.from(this.salesRegionExceptionMap.values());
		return salesRegionExceptions;
	}

	/** Determines if the current sales region exception is the same as the base exception */
	@computed get sameAsBaseComposition(): boolean {
		if (this.tabIndex === 0) {
			return true;
		}
		const currentException = this.salesRegionExceptions[this.tabIndex];
		const baseException = this.salesRegionExceptions[0];
		if (baseException) {
			return isEqual(currentException.compareObject, baseException.compareObject);
		}
		return false;
	}

	@action removeDuplicateSalesRegionExceptions = (): void => {
		if (this.salesRegionExceptions.length < 2) {
			return;
		}
		const baseExceptionCompareObject = this.salesRegionExceptions[0].compareObject;
		this.salesRegionExceptions.forEach((exception, index) => {
			if (index !== 0 && isEqual(exception.compareObject, baseExceptionCompareObject)) {
				this.salesRegionExceptionMap.delete(exception.key);
			}
		});
		if (this.tabIndex >= this.salesRegionExceptions.length) {
			this.tabIndex = 0;
		}
	};

	deepCopyPlacement(key: string, placement: CompositionPlacement): CompositionPlacement {
		const newPlacement = new CompositionPlacement(this, key, placement.sortValue, placement.placementItemsForDropdown, placement.currentUrl);
		if (this.fromStyleBom) {
			placement.materialBOMs.forEach((material, index) => {
				newPlacement.addBOMMaterialWithPercentageAndUrl(material.percentage, material.currentUrl, false);
				material.baseCompositionMaterials.forEach((composition) => {
					newPlacement.materialBOMs[index].loadBaseCompositionMaterial(composition.percentage, composition.currentUrl, composition.currentType, composition.nestUnderContainsPlacement);
				});
			});
		} else {
			placement.baseCompositionMaterials.forEach((composition) => {
				newPlacement.loadBaseCompositionMaterial(composition.percentage, composition.currentUrl, composition.currentType, composition.nestUnderContainsPlacement);
			});
		}
		return newPlacement;
	}

	/** Provided with a name and an index from which to copy, a new sales region is created. 
	 * changeTab should be true to cause the dialog to automatically move to the new tab, or false if not */
	@action addSalesRegionException = (name: string, currentUrl: cnl, copyFromIndex: number = 0, changeTab: boolean = true): string => {
		const key = createSalesRegionExceptionKey();
		this.salesRegionExceptionMap.set(key, new SalesRegionException(this, key, name, currentUrl));
		const newSRE = this.salesRegionExceptionMap.get(key);
		if (name === BASE_COMPOSITION) {
			newSRE.addCompositionPlacement();
		} else if (copyFromIndex >= 0) {
			this.copySalesRegionContent(this.salesRegionExceptions[copyFromIndex], newSRE);
		}
		if (changeTab) {
			this.tabIndex = this.salesRegionExceptions.length - 1;
		}
		return key;
	};

	copySalesRegionContent = (srSource: SalesRegionException, srDest: SalesRegionException): void => {
		srDest.clearCompositionPlacementMap();
		srSource.compositionPlacementMap.forEach((placement) => {
			const newPlacementKey = createCompositionPlacementKey();
			srDest.addDeepCopyPlacement(newPlacementKey, this.deepCopyPlacement(newPlacementKey, placement));
		});
		srDest.setCombineSameCompositionMaterials(null, srSource.combineSameCompositionMaterials);
		srDest.setIncludeCompositionTypes(null, srSource.includeCompositionTypes);
		srDest.setIncludeCompositionPlacementNames(null, srSource.includeCompositionPlacementNames);
		srDest.setShowSettingsWithBoolean(srSource.showSettings);
	};

	@action addApplyToCheckBox = (name: string): void => {
		this.applyToSalesRegionChecks.add(name);
	};

	@action removeApplyToCheckBox = (name: string): void => {
		this.applyToSalesRegionChecks.delete(name);
	};

	/** remove composition placement */
	@action removeSalesRegionException = (key: string): void => {
		const currentSalesRegionException = this.salesRegionExceptionMap.get(key);
		const removeIndex = this.salesRegionExceptions.indexOf(currentSalesRegionException);
		if (removeIndex <= this.tabIndex) {
			this.tabIndex -= 1;
		}
		this.salesRegionExceptionMap.delete(key);
	};

	/** get the names of the sales regions */
	get salesRegionExceptionNames(): string[] {
		return this.salesRegions.map((sr) => sr.$Name);
	}
	/** Gets all new dropdowns excluding the existing SRE C8Nodes */
	get salesRegionDropdown(): C8Node[] {
		const existingNames = new Set(this.salesRegionExceptions.map((exception) => exception.name));
		const unusedC8Nodes = this.salesRegions.filter((sr) => !existingNames.has(sr.$Name));
		const sortedUnusedC8Nodes = unusedC8Nodes.sort((a, b) => a.$Name.localeCompare(b.$Name));
		return sortedUnusedC8Nodes;
	}

	/** Gets all new dropdowns excluding the current SRE */
	get applyToSalesRegionNameDropdown(): string[] {
		const { salesRegionExceptionNames, salesRegionExceptions, tabIndex } = this;
		return salesRegionExceptionNames.filter((sreName) => sreName !== salesRegionExceptions[tabIndex].name);
	}

	/** Determines wether or not the Add SRE button should be enabled or disabled */
	get disableAddSalesRegionException(): boolean {
		return this.salesRegionDropdown.length === 0;
	}

	/** Gets the name of the SRE we are deleting for the localized prompt */
	get salesRegionToDeleteName(): string[] {
		return [this.salesRegionExceptionMap.get(this.currentSalesRegionDeleteKey)?.name];
	}

	onCancelDeleteConfirm = (event?: React.MouseEvent<HTMLButtonElement>): void => {
		event.stopPropagation();
	};

	onCloseDeleteConfirm = (): void => {
		this.setShowSalesRegionConfirmCloseWindow(false);
	};
	@action onClickDeleteConfirm = (key: string): ((event?: React.MouseEvent<HTMLButtonElement>) => void) => {
		return (event?: React.MouseEvent<HTMLButtonElement>): void => {
			event.stopPropagation();
			this.currentSalesRegionDeleteKey = key;
			this.setShowSalesRegionConfirmCloseWindow(true);
		};
	};

	onConfirm = (event: React.MouseEvent<HTMLButtonElement>): void => {
		event.stopPropagation();
		this.removeSalesRegionException(this.currentSalesRegionDeleteKey);
	};

	/** Handles the click event on the save confirmation popup */
	onClickConfirmOnSaveConfirmationPopup = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
		await this.finalSave();
	};

	/** returns the BannerConditions string array after deleting any that are not applicable.*/
	get currentBannerConditions(): BannerCondition[] {
		const { bannerConditions, fromStyleBom, salesRegionExceptions } = this;

		bannerConditions.forEach((condition) => {
			if (!salesRegionExceptions.some((exception) => {
				return condition.sreName == exception.name;
			})) {
				this.removeBannerCondition(condition);
			}
		});

		salesRegionExceptions.forEach(({
			name,
			bomMaterialHasNullPercent,
			bomMaterialHasZeroPercent,
			bomMaterialOrCompositionMaterialContainsDUMMY,
			compositionMaterialHasNullPercent,
			compositionMaterialHasZeroPercent,
			everyPlacementIsZero,
			everyPlacementContainsNull,
			somePlacementNotHaveSelectedMaterials,
			noCompositionOnSomeBOMMaterial,
			somePlacementNot100Percent,
		}) => {
			if (!compositionMaterialHasNullPercent && (this.allowZeroPercent || !compositionMaterialHasZeroPercent)) {
				this.removeBannerCondition(new BannerCondition(name, "BannerSpecifyPercentagePerCompositionMaterial"));
			}
			if (everyPlacementContainsNull) {
				bannerConditions.delete("BannerEnsureTotalPercentagePerPlacement");
			}
			if (fromStyleBom) {
				if (!!this.currentSelectedBOM) {
					this.removeBannerCondition(new BannerCondition(BASE_COMPOSITION, "BannerSelectStyleBOM"));
				}
				if (!bomMaterialHasNullPercent && (this.allowZeroPercent || !bomMaterialHasZeroPercent)) {
					this.removeBannerCondition(new BannerCondition(name, "BannerSpecifyPercentagePerBOMMaterial"));
				}
				if (!somePlacementNotHaveSelectedMaterials) {
					this.removeBannerCondition(new BannerCondition(name, "BannerEnsurePlacementHasBOMMaterial"));
				}
				if (!somePlacementNot100Percent || everyPlacementIsZero) {
					this.removeBannerCondition(new BannerCondition(name, "BannerEnsureTotalPercentagePerPlacement"));
				}
				if (!bomMaterialOrCompositionMaterialContainsDUMMY) {
					this.removeBannerCondition(new BannerCondition(name, "BannerSpecifyBOMMaterials"));
				}
				if (bomMaterialOrCompositionMaterialContainsDUMMY || !noCompositionOnSomeBOMMaterial) {
					this.removeBannerCondition(new BannerCondition(name, "BannerEnsureBOMMaterialHasComposition"));
				}
			} else {
				if (!somePlacementNotHaveSelectedMaterials) {
					this.removeBannerCondition(new BannerCondition(name, "BannerEnsurePlacementHasCompositionMaterial"));
				}
				if (!somePlacementNot100Percent) {
					this.removeBannerCondition(new BannerCondition(name, "BannerEnsureTotalPercentagePerPlacement"));
				}
				if (!bomMaterialOrCompositionMaterialContainsDUMMY) {
					this.removeBannerCondition(new BannerCondition(name, "BannerSpecifyCompositionMaterials"));
				}
			}
		});

		return Array.from(bannerConditions.values());
	}

	/** Initiates the save action and sets the save flag to true */
	@action mainDialogOnSave = async (): Promise<boolean> => {
		this.loadBannerConditions();
		if (this.bannerConditions.size > 0) {
			return false;
		}
		this.saveFlag = true;
		if (this.hasZeroPercentNotNestUnder) {
			return false;
		} else {
			await this.finalSave();
			return true;
		}
	};

	/** Calculates a unique key for a given banner condition */
	calculateKey(condition: BannerCondition): string {
		return `${condition.sreName}_${condition.bannerTitle}`;
	}

	searchKeyInBannerConditions = (substring: string): boolean => {
		const keys = this.bannerConditions.keys();
		for (const key of keys) {
			if (key.includes(substring)) {
				return true;
			}
		}
		return false;
	};

	/** Removes a banner condition from the map if it exists */
	removeBannerCondition(newCondition: BannerCondition): void {
		const key = this.calculateKey(newCondition);
		this.bannerConditions.delete(key);
	}

	/** Adds a new banner condition to the map if it does not already exist, using a combined key of sreName and bannerTitle. */
	addBannerCondition(newCondition: BannerCondition): void {
		const key = this.calculateKey(newCondition);
		if (!this.bannerConditions.has(key)) {
			this.bannerConditions.set(key, newCondition);
		}
	}

	/** Loads the banners when save is clicked. Ensures validation is passed*/
	@action loadBannerConditions = (): void => {
		const {
			allowCompositionEdit,
			allowZeroPercent,
			enforceHundredPercent,
			fromStyleBom,
			salesRegionExceptions,
		} = this;

		salesRegionExceptions.forEach(({
			name,
			bomMaterialHasZeroPercent,
			bomMaterialHasNullPercent,
			bomMaterialOrCompositionMaterialContainsDUMMY,
			compositionMaterialHasNullPercent,
			compositionMaterialHasZeroPercent,
			everyPlacementIsZero,
			everyPlacementContainsNull,
			somePlacementNotHaveSelectedMaterials,
			noCompositionOnSomeBOMMaterial,
			somePlacementNot100Percent,
		}) => {
			if (fromStyleBom) {
				if (!this.currentSelectedBOM) {
					this.addBannerCondition(new BannerCondition(BASE_COMPOSITION, "BannerSelectStyleBOM"));
				}
				if (bomMaterialOrCompositionMaterialContainsDUMMY) {
					this.addBannerCondition(new BannerCondition(name, "BannerSpecifyBOMMaterials"));
				}
				if (bomMaterialHasNullPercent || (!allowZeroPercent && bomMaterialHasZeroPercent)) {
					this.addBannerCondition(new BannerCondition(name, "BannerSpecifyPercentagePerBOMMaterial"));
				}
				if (somePlacementNotHaveSelectedMaterials) {
					this.addBannerCondition(new BannerCondition(name, "BannerEnsurePlacementHasBOMMaterial"));
				}
				if (enforceHundredPercent && somePlacementNot100Percent && !everyPlacementIsZero && !everyPlacementContainsNull) {
					this.addBannerCondition(new BannerCondition(name, "BannerEnsureTotalPercentagePerPlacement"));
				}
				if (!bomMaterialOrCompositionMaterialContainsDUMMY && noCompositionOnSomeBOMMaterial) {
					this.addBannerCondition(new BannerCondition(name, "BannerEnsureBOMMaterialHasComposition"));
				}
				if (compositionMaterialHasNullPercent || allowCompositionEdit && !allowZeroPercent && compositionMaterialHasZeroPercent) {
					this.addBannerCondition(new BannerCondition(name, "BannerSpecifyPercentagePerCompositionMaterial"));
				}
			} else {
				if (compositionMaterialHasNullPercent || !allowZeroPercent && compositionMaterialHasZeroPercent) {
					this.addBannerCondition(new BannerCondition(name, "BannerSpecifyPercentagePerCompositionMaterial"));
				}
				if (somePlacementNotHaveSelectedMaterials) {
					this.addBannerCondition(new BannerCondition(name, "BannerEnsurePlacementHasCompositionMaterial"));
				} else if (enforceHundredPercent && somePlacementNot100Percent && !everyPlacementContainsNull) {
					this.addBannerCondition(new BannerCondition(name, "BannerEnsureTotalPercentagePerPlacement"));
				}
				if (bomMaterialOrCompositionMaterialContainsDUMMY) {
					this.addBannerCondition(new BannerCondition(name, "BannerSpecifyCompositionMaterials"));
				}
			}
		});
	};

	/** Checking Zero percentage NestUnderContainsPlacement */
	@computed get hasZeroPercentNotNestUnder(): boolean {
		const { compositionPlacements } = this.salesRegionExceptions[this.tabIndex];
		const comPlace = compositionPlacements?.find((element) => {
			if (this.fromStyleBom) {
				return element.materialBOMs.find(({ baseCompositionMaterials }) =>
					baseCompositionMaterials.find(({ nestUnderContainsPlacement, percentage }) => !nestUnderContainsPlacement && percentage === 0),
				);
			} else {
				return element.baseCompositionMaterials.find(({ nestUnderContainsPlacement, percentage }) => !nestUnderContainsPlacement && percentage === 0);
			}
		});
		return comPlace != null;
	};

	/** Calling OnSave from here */
	finalSave = async (): Promise<void> => {
		await this.onSave();
		runInAction(() => {
			this.saveFlag = false;
		});

		this._onClose();
	};

	/** Closes the save confirmation popup and sets the save flag to false */
	@action onCloseSaveConfirmPopup = (): void => {
		this.saveFlag = false;
	};

	/** Handles the cancel action on the main dialog */
	mainDialogOnCancel = (): void => {
		this._onClose();
	};

	@action handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
		this.menuAnchorEl = event.currentTarget;
	};

	@action handleMenuClose = (): void => {
		this.menuAnchorEl = null;
	};

	@action handleApplyToMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
		this.applyToAnchorEl = event.currentTarget;
	};

	@action resetExceptionOnClick = (event: React.MouseEvent<HTMLElement>): void => {
		this.copySalesRegionContent(this.salesRegionExceptions[0], this.salesRegionExceptions[this.tabIndex]);
	};

	/** when closing the Apply To Menu the sales regions selected will be loaded with the current placement values */
	@action handleApplyToMenuClose = (event: React.MouseEvent<HTMLElement>): void => {
		const { applyToSalesRegionChecks, salesRegionExceptions, tabIndex } = this;
		event.stopPropagation();
		this.applyToAnchorEl = null;
		applyToSalesRegionChecks.forEach((sr: string) => {
			const existingSRToApply = salesRegionExceptions.find((sre) => sre.name === sr);
			const salesRegionNode = this.salesRegions.find((srNode) => srNode.$Name === sr);
			if (!existingSRToApply) {
				this.addSalesRegionException(sr, salesRegionNode.$URL, tabIndex, false);
			} else {
				this.copySalesRegionContent(this.salesRegionExceptions[tabIndex], existingSRToApply);
			}
		});
		if (applyToSalesRegionChecks.size > 0) {
			const applyToSalesRegionChecksArray = Array.from(applyToSalesRegionChecks);
			const lastSalesRegionChecked = applyToSalesRegionChecksArray[applyToSalesRegionChecksArray.length - 1];
			const gotoIndex = this.salesRegionExceptions.findIndex((sre) => sre.name === lastSalesRegionChecked);
			this.tabIndex = gotoIndex;
		}
		// Delayed action to occur after the dialog closes
		setTimeout(() => {
			applyToSalesRegionChecks.clear();
		}, 500);
	};

	@action onTabChange = (event: React.MouseEvent<HTMLButtonElement>, newValue: number): void => {
		if (event.defaultPrevented) {
			return;
		}
		this.tabIndex = newValue;
	};

	@action setShowSalesRegionConfirmCloseWindow = (show: boolean): void => {
		this.showSalesRegionConfirmCloseWindow = show;
	};

	getDisableAddCompositionMaterial = (parent: CompositionPlacement | MaterialOnBOM, compositionMaterialsHaveDummy: boolean): boolean => {
		return compositionMaterialsHaveDummy || (this.fromStyleBom && (!this.currentSelectedBOM || parent.currentUrl === DUMMY));
	};

	/** prevent deduplication by loading placements here */
	loadPlacement = (placementNode: C8Node, placementKey: string, sre: SalesRegionException, isBase: boolean): void => {
		const technicalCompositionNodes = isBase ? this.existingBaseTechnicalCompositionNodes : this.existingSRETechnicalCompositionNodes;
		if (this.fromStyleBom) {
			const materialNodes = isBase ? this.existingBaseMaterialNodes : this.existingSREMaterialNodes;
			placementNode.CareAndCompositionLines.forEach((materialCNL) => {
				const currentMaterialNode = materialNodes.find((node) => node.$URL === materialCNL);
				const placementToAdd = sre.compositionPlacementMap.get(placementKey);
				if (placementToAdd) {
					const baseMaterialKey = placementToAdd.addBOMMaterialWithPercentageAndUrl(currentMaterialNode.PercentOfTotal * 100, currentMaterialNode.MaterialFromBOM, false);
					currentMaterialNode.TechnicalComposition.forEach((compositionCNL) => {
						const currentCompositionNode = technicalCompositionNodes.find((node) => node.$URL === compositionCNL);
						const compositionToAdd = placementToAdd.materialBOMMap.get(baseMaterialKey);
						compositionToAdd.loadBaseCompositionMaterial(
							currentCompositionNode.Percentage * 100,
							currentCompositionNode.Composition,
							currentCompositionNode.SustainabilityCompositionType,
							currentCompositionNode.ExemptFromPercentage,
						);
					});
				}
			});
		} else {
			placementNode.TechnicalComposition.forEach((compositionCNL) => {
				const currentCompositionNode = technicalCompositionNodes.find((node) => node.$URL === compositionCNL);
				const placementToAdd = sre.compositionPlacementMap.get(placementKey);
				if (placementToAdd) {
					placementToAdd.loadBaseCompositionMaterial(
						currentCompositionNode.Percentage * 100,
						currentCompositionNode.Composition,
						currentCompositionNode.SustainabilityCompositionType,
						currentCompositionNode.ExemptFromPercentage,
					);
				}
			});
		}
	};

	/** load all main dialog data */
	loadMainDialog = (): void => {
		this.addSalesRegionException(BASE_COMPOSITION, DUMMY);
		// only load placements if there are placements in the view
		if (this.existingBasePlacementNodes.length > 0) {
			// load base first
			this.salesRegionExceptions[0].clearCompositionPlacementMap();
			this.salesRegionExceptions[0].setCombineSameCompositionMaterials(null, this.existingRevision.CombineSameCompositionMaterials);
			this.salesRegionExceptions[0].setIncludeCompositionPlacementNames(null, this.existingRevision.IncludeCompositionPlacementNames);
			this.salesRegionExceptions[0].setIncludeCompositionTypes(null, this.existingRevision.IncludeCompositionTypes);
			this.existingBasePlacementNodes.forEach((placement) => {
				const basePlacementKey = this.salesRegionExceptions[0].addCompositionPlacement(placement.Placement);
				this.loadPlacement(placement, basePlacementKey, this.salesRegionExceptions[0], true);
			});
			// load all exceptions
			this.existingSRENodes.forEach((sreNode) => {
				const srUrl = sreNode.SalesRegion;
				const srNode = this.existingSRNodes.find((srNode) => srNode.$URL === srUrl);
				const salesRegionKey = this.addSalesRegionException(srNode.$Name, srUrl, -1, false);
				const currentSalesRegionException = this.salesRegionExceptionMap.get(salesRegionKey);
				currentSalesRegionException.setCombineSameCompositionMaterials(null, sreNode.CombineSameCompositionMaterials);
				currentSalesRegionException.setIncludeCompositionPlacementNames(null, sreNode.IncludeCompositionPlacementNames);
				currentSalesRegionException.setIncludeCompositionTypes(null, sreNode.IncludeCompositionTypes);
				sreNode.SalesRegionItems.forEach((placementCNL) => {
					const placementNode = this.existingSREPlacementNodes.find((placement) => placement.$URL === placementCNL);
					const placementKey = currentSalesRegionException.addCompositionPlacement(placementNode.Placement);
					this.loadPlacement(placementNode, placementKey, currentSalesRegionException, false);
				});
			});
		} else if (!this.fromStyleBom) {
			this.salesRegionExceptions[0].compositionPlacements[0].addBaseCompositionMaterial();
			this.focusDropDownBoolean = false;
		}
		if (!!this.currentSalesRegionExceptionName) {
			const exceptionIndex = this.salesRegionExceptions.findIndex(
				(exception) => exception.name === this.currentSalesRegionExceptionName,
			);
			if (exceptionIndex !== -1) {
				this.tabIndex = exceptionIndex;
			}
		}
	};

	/** Sets the current URL for a CompositionPlacement based on a selected ComponentItem. */
	@action setCurrentBOM = async (event: React.ChangeEvent<{}>, item: ComponentItem): Promise<void> => {
		this.currentSelectedBOM = this.apparelBOMNodes.find((bom) => xml.nodeGetURL(bom) === item.id);
		// TODO: Readd and test after onSave is implemented
		// const existingMaterialUrls = this.salesRegionExceptions[0].compositionPlacements.flatMap((placement) =>
		// 	placement.materialBOMs.map(({ currentUrl }) => currentUrl),
		// );
		// await resolveNodes(existingMaterialUrls, this.cache);
		await this.insertMaterialNodesIfDoesntExist(this.currentSelectedBOM.$URL);
	};



	/** Retrieves an array of all available ComponentItems for placement, including the DUMMY item. */
	get allBOMsOnStyle(): ComponentItem[] {
		return this.apparelBOMNodes
			.map((node) => ({
				id: xml.nodeGetURL(node),
				node,
			}));
	}

	/** Getter for materialNodes */
	get materialNodes(): C8Node[] {
		return this.materialNodeMap.get(this.currentSelectedBOM?.$URL) || [];
	}

	/** returns the current Apparel BOM that is selected as a Component Item for the renderer */
	@computed get apparelBOMValueAsComponentItem(): ComponentItem {
		return this.allBOMsOnStyle.find((bomItem) => bomItem.id === xml.nodeGetURL(this.currentSelectedBOM));
	}

	/** returns the current Apparel BOM that is selected as a Component Item for the renderer */
	getApparelBOMValueAsComponentItemFromCNL = (urlToFind: cnl): ComponentItem => {
		return this.allBOMsOnStyle.find((bomItem) => bomItem.id === urlToFind);
	};

	/** goToDialog onClick that opens with a node list */
	onClickApparelBOMGoToDialog = (e: SyntheticEvent): void => {
		const { allBOMsOnStyle, bomGoToDialogHelper, currentSelectedBOM } = this;
		const bomCNLs = allBOMsOnStyle.map((bom) => bom.id);
		const selections = currentSelectedBOM ? [currentSelectedBOM] : [];
		bomGoToDialogHelper.openGoToDialogWithCNLs(bomCNLs, selections);
	};

	getSelectActions = (onClick: (e: SyntheticEvent) => void): JSX.Element[] => {
		return [
			<Button
				design="link"
				onClick={onClick}
				res="GoToDialog"
			/>,
		];
	};

	/** if a query is needed perform one, otherwise do nothing */
	async insertMaterialNodesIfDoesntExist(apparelBOMcnl: cnl): Promise<void> {
		if (!this.materialNodeMap.has(apparelBOMcnl)) {
			const response = await loadNodes("GetMaterialNodes", { CurrentApparelBOMNode: apparelBOMcnl });
			const materialNodes = response.getResultNodes();
			runInAction(() => {
				this.materialNodeMap.set(apparelBOMcnl, materialNodes);
			});
		}
	}

	constructor(
		fromEdit: boolean,
		currentSalesRegionExceptionName: string,
		loc: ILocaleProvider,
		onSave: ControllerSaveFunction,
		onClose: VoidFunction,
		hasSustainability: boolean,
		fromStyleBom: boolean,
		careCompRevNode: C8Node,
		salesRegions: C8Node[],
		cache: ICache,
	) {
		super();
		makeObservable(this);
		this.loc = loc;
		this.currentSalesRegionExceptionName = currentSalesRegionExceptionName;
		this.hasSustainability = hasSustainability;
		this.fromStyleBom = fromStyleBom;
		this.careCompRevNode = careCompRevNode;
		this._onSave = onSave;
		this._onClose = onClose;
		this.cache = cache;
		this.salesRegions = salesRegions;
		this.bomGoToDialogHelper = new GoToDialogHelper(
			"ApparelBOM",
			false,
			(selectedUrl: cnl[]) => {
				this.focusDropDownBoolean = false;
				this.setCurrentBOM(null, this.allBOMsOnStyle.find((item) => item.id === selectedUrl[0]));
			},
		);
		this.init(fromEdit);
	}

	async init(fromEdit: boolean): Promise<void> {
		const { cache } = this;
		const careCompNode = cache.get(this.careCompRevNode.__Parent__);
		const styleCNL = careCompNode.__Parent__;
		const careAndCompositionSubType = careCompNode[ATTR_SUBTYPE];
		const bomUrl = cache.get(this.careCompRevNode.__Parent__).BOM;
		if (fromEdit) {
			const careCompRevCNL = this.careCompRevNode.$URL;
			runInAction(() => {
				this.existingRevision = cache.get(careCompRevCNL);
				this.existingBasePlacementNodes = cache.getPathNodes(careCompRevCNL, "Child:Items", null, true);
				this.existingBaseMaterialNodes = cache.getPathNodes(careCompRevCNL, "Child:Items/Child:CareAndCompositionLines", null, true);
				let technicalPath = "Child:Items/Child:CareAndCompositionLines/Child:TechnicalComposition";
				if (this.existingBaseMaterialNodes.length === 0) {
					technicalPath = "Child:Items/Child:TechnicalComposition";
					this.fromStyleBom = false;
				} else {
					this.fromStyleBom = true;
				}
				this.existingBaseTechnicalCompositionNodes = cache.getPathNodes(careCompRevCNL, technicalPath, null, true);
				// load all existing sales region exceptions
				this.existingSRENodes = cache.getPathNodes(careCompRevCNL, "Child:SalesRegionException", null, true);
				// filter them to only show sales region exceptions different from the base
				this.existingSRENodes = this.existingSRENodes.filter((sreNode) => sreNode.SalesRegionItems.length !== 0);
				// load all existing sales regions on the exceptions
				this.existingSRNodes = cache.getPathNodes(careCompRevCNL, "Child:SalesRegionException/Child:SalesRegion", null, true);
				this.existingSREPlacementNodes = cache.getPathNodes(careCompRevCNL, "Child:SalesRegionException/Child:SalesRegionItems", null, true);
				if (this.fromStyleBom) {
					this.existingSREMaterialNodes = cache.getPathNodes(careCompRevCNL, "Child:SalesRegionException/Child:SalesRegionItems/Child:CareAndCompositionLines", null, true);
					this.existingSRETechnicalCompositionNodes = cache.getPathNodes(careCompRevCNL, "Child:SalesRegionException/Child:SalesRegionItems/Child:CareAndCompositionLines/Child:TechnicalComposition", null, true);
				} else {
					this.existingSRETechnicalCompositionNodes = cache.getPathNodes(careCompRevCNL, "Child:SalesRegionException/Child:SalesRegionItems/Child:TechnicalComposition", null, true);
				}
			});
		}

		if (this.fromStyleBom) {
			const apparelBOMResponse = await loadNodes("QueryApparelNodes", { StyleCNL: styleCNL });
			runInAction(() => {
				const apparelBOMNodesFromQuery = apparelBOMResponse.getResultNodes();
				this.apparelBOMNodes = apparelBOMNodesFromQuery ?? [];
			});
			if (bomUrl != DUMMY) {
				this.setCurrentBOM(null, this.getApparelBOMValueAsComponentItemFromCNL(bomUrl));
			}
		}
		runInAction(() => {
			this.enforceHundredPercent = reflection.getSubtypeSetting(careAndCompositionSubType, "EnforceHundredPercent");
		});
		this.allowZeroPercent = reflection.getSubtypeSetting(careAndCompositionSubType, "AllowZeroPercent");
		this.allowCompositionEdit = reflection.getSubtypeSetting(careAndCompositionSubType, "AllowCompositionEdits");
		this.languageSyntax = reflection.getSubtypeSetting(careAndCompositionSubType, "LanguageSyntax");
		const staticNodeResponse = await loadNodes("QueryStaticNodes");
		runInAction(() => {
			const placementNodesFromQuery = staticNodeResponse.getNodesOfType("CompositionPlacement");
			const compositionNodesFromQuery = staticNodeResponse.getNodesOfType("Composition");
			const sustainabilityCompositionNodesFromQuery = staticNodeResponse.getNodesOfType("SustainabilityCompositionType");
			this.placementNodes = placementNodesFromQuery ?? [];
			this.compositionNodes = compositionNodesFromQuery ?? [];
			this.sustainabilityCompositionNodes = sustainabilityCompositionNodesFromQuery ?? [];
		});
		this.loadMainDialog();
	}
}

export const BaseCompositionStoreContext = createContext<BaseCompositionStore>(undefined);
export const useBaseCompositionStore = () => useContext(BaseCompositionStoreContext);
